'use client';

/**
 * Sorsy-branded override of the generic `KitEnquiryCta` block (see
 * `@commerce/site-builder-blocks/commerce/KitEnquiryCta.tsx` for the
 * host-agnostic version this reskins). Registered under the same
 * `KitEnquiryCta` block-type key in {@link sorsyTheme}, so an operator's
 * existing page JSON for that block type renders through this component
 * with no changes — same props, different visual treatment.
 *
 * <h3>Why this exists as a theme override, not just the generic block</h3>
 * Sorsy sells £4-6k renewables kits through cold outreach, not a
 * marketplace funnel (see the "renewables sales are cold outreach" project
 * note) — the site's one job is getting a visitor to pick up the phone.
 * The generic CTA card treats the phone link as a secondary action beside
 * the button; this version makes the phone number the loudest thing on
 * the page — full-bleed band, oversized tel: link, no card chrome to
 * compete with it.
 *
 * <p>Still reads through {@code useCommerceSource()} exactly like the
 * generic block (never {@code fetch}), and still never adds anything to a
 * basket — there is no checkout port on the contract and this component
 * doesn't invent one.
 *
 * <h3>Contrast</h3>
 * The accent fallback below (`#b45309`, an amber-700) was checked, not
 * assumed: white text on `#b45309` measures ~5.03:1 by the WCAG relative
 * luminance formula, clearing the 4.5:1 AA floor for normal-size text with
 * headroom — unlike the platform's own `#7367f0` (a spec-note flagged that
 * one fails). The tenant's real configured brand token overrides this
 * fallback in production; this is only what renders before that token is
 * set.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommerceSource, PortalLink, tx } from '@akademi/site-builder-runtime';
import { BlockRoot, AdapterPlaceholder, useAsyncResource } from '@akademi/site-builder-blocks';
import { ProductThumb, ProductPrice } from '@commerce/site-builder-blocks';

type Localized = string | Record<string, string>;

export interface KitEnquiryCtaProps {
  title?: Localized;
  subtitle?: Localized;
  productSlug?: string;
  showPrice?: boolean;
  ctaLabel?: Localized;
  ctaHref?: string;
  phoneNumber?: string;
  phoneLabel?: Localized;
  note?: Localized;
}

const ACCENT = '#b45309';
const ACCENT_DARK = '#92400e';

export function KitEnquiryCta({
  title,
  subtitle,
  productSlug,
  showPrice = true,
  ctaLabel,
  ctaHref = '/contact',
  phoneNumber,
  phoneLabel,
  note,
}: KitEnquiryCtaProps) {
  const commerce = useCommerceSource();
  const { t } = useTranslation('theme-sorsy');

  const state = useAsyncResource(async () => {
    if (!commerce || !productSlug) return null;
    return commerce.getProductBySlug(productSlug);
  }, [commerce, productSlug]);

  const headline = tx(title) || t('enquiry.title');
  const sub = tx(subtitle) || t('enquiry.subtitle');
  const cta = tx(ctaLabel) || t('enquiry.cta');
  const phoneText = tx(phoneLabel) || t('enquiry.callUs');
  const noteText = tx(note) || t('enquiry.note');

  const boundToProduct = Boolean(productSlug);
  const product = state.kind === 'loaded' ? state.data : null;

  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={innerStyle}>
        <div style={contentStyle}>
          <span style={eyebrowStyle}>{t('enquiry.eyebrow')}</span>
          <h2 style={titleStyle}>{headline}</h2>
          {sub && <p style={subtitleStyle}>{sub}</p>}

          {boundToProduct && (
            <div style={productRowStyle}>
              {!commerce ? (
                <AdapterPlaceholder
                  blockType="KitEnquiryCta"
                  adapterName="CommerceSource"
                  reason={t('enquiry.adapterReason')}
                />
              ) : state.kind === 'loading' ? (
                <div
                  className="theme-skeleton"
                  style={{ width: 220, height: 20, borderRadius: 4 }}
                />
              ) : product ? (
                <>
                  <ProductThumb
                    image={product.heroImage}
                    aspect="1 / 1"
                    style={{ width: 56, height: 56, flexShrink: 0 }}
                  />
                  <div>
                    <div style={productTitleStyle}>{product.title}</div>
                    {showPrice && product.price && (
                      <div style={{ marginTop: 2 }}>
                        <span style={fromLabelStyle}>{t('enquiry.fromPrice')} </span>
                        <ProductPrice price={product.price} size="sm" />
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </div>
          )}

          {phoneNumber && (
            <PortalLink href={`tel:${phoneNumber}`} style={phoneStyle}>
              <span aria-hidden style={phoneIconStyle}>
                ☎
              </span>
              <span>
                <span style={phoneCaptionStyle}>{phoneText}</span>
                <span style={phoneNumberStyle}>{phoneNumber}</span>
              </span>
            </PortalLink>
          )}

          <PortalLink href={ctaHref} style={ctaButtonStyle}>
            {cta}
          </PortalLink>

          {noteText && <p style={noteStyle}>{noteText}</p>}
        </div>
      </div>
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  padding: 'clamp(40px, 6vw, 80px) var(--space-6, 24px)',
  background: `linear-gradient(135deg, ${ACCENT_DARK}, ${ACCENT})`,
  color: '#fff',
};

const innerStyle: CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
};

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  alignItems: 'flex-start',
};

const eyebrowStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  opacity: 0.85,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(28px, 4vw, 40px)',
  fontWeight: 800,
  lineHeight: 1.15,
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-md, 16px)',
  lineHeight: 1.5,
  opacity: 0.92,
  maxWidth: 560,
};

const productRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 10px)',
  background: 'rgba(255,255,255,0.12)',
};

const productTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
};

const fromLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  opacity: 0.85,
};

const phoneStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 12,
  textDecoration: 'none',
  color: '#fff',
};

const phoneIconStyle: CSSProperties = {
  fontSize: 28,
};

const phoneCaptionStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs, 12px)',
  opacity: 0.85,
};

const phoneNumberStyle: CSSProperties = {
  display: 'block',
  fontSize: 'clamp(22px, 3vw, 30px)',
  fontWeight: 800,
  letterSpacing: '0.01em',
};

const ctaButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-3, 14px) var(--space-6, 28px)',
  background: '#fff',
  color: ACCENT_DARK,
  borderRadius: 'var(--radius-pill, 9999px)',
  fontSize: 'var(--font-size-sm, 15px)',
  fontWeight: 700,
  textDecoration: 'none',
};

const noteStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs, 12px)',
  opacity: 0.8,
};
